import { useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { PlaceAutocomplete } from './PlaceAutocomplete';
import type { ExternalPlace, Poi } from '../api/types';

/**
 * Search for a place, then add it — as two steps rather than one.
 *
 * The multi-select this replaces committed on the click that chose a place,
 * and kept what had been chosen as chips inside its own box. Two problems
 * with that: the box grew a line per place until the field being typed into
 * was pushed off its own control, and a stray click on a suggestion added a
 * place with no moment in between to notice.
 *
 * So the box holds one candidate at a time and the list lives underneath,
 * where each entry carries its own stay time or distance and its own way off
 * the list. Adding is a button, which is also what makes it reachable from a
 * keyboard without a listbox open.
 */
export function PlacePicker({
  cityId, allCities, label, placeholder, visitableOnly, avoidableOnly, categoryKeys,
  value, onChange, max = 20, onExternalPick, disabled, reserved,
}: {
  cityId: string;
  /** Search every city, not only the one selected. */
  allCities?: boolean;
  label: string;
  placeholder?: string;
  visitableOnly?: boolean;
  avoidableOnly?: boolean;
  categoryKeys?: string[];
  /** The list being added to. Rendered by the caller, not here. */
  value: Poi[];
  /**
   * Places that are already something else on this round, and so cannot be
   * added here — the start and the destination.
   *
   * A stop is somewhere the route passes BETWEEN its two ends. Naming one of
   * the ends as a stop as well asks the planner to visit a place it is
   * already at, which it answers by scheduling a second arrival at the same
   * coordinates. Refused where it is chosen, with the reason, rather than
   * silently dropped later.
   */
  reserved?: { id: string; why: string }[];
  onChange: (pois: Poi[]) => void;
  max?: number;
  /**
   * An external result has no database id, so the caller turns one into a
   * pinned coordinate. Chosen results are added straight away — there is no
   * `Poi` for the button to hold on to.
   */
  onExternalPick?: (place: ExternalPlace) => void;
  disabled?: boolean;
}) {
  const [candidate, setCandidate] = useState<Poi | null>(null);

  const atLimit = value.length >= max;
  const already = candidate ? value.some((p) => p.id === candidate.id) : false;
  const taken = candidate
    ? reserved?.find((r) => r.id === candidate.id) ?? null
    : null;

  const add = () => {
    if (!candidate || already || atLimit || taken) return;
    onChange([...value, candidate]);
    setCandidate(null);
  };

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
      {/*
        The field takes the row and the button takes what it needs.

        Without this the autocomplete has no flex basis of its own inside a
        row, so it collapses to the width of its own label and the search box
        becomes unusable — which is exactly what happened.
      */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <PlaceAutocomplete
          cityId={cityId}
          {...(allCities ? { allCities: true } : {})}
          label={label}
          {...(placeholder ? { placeholder } : {})}
          {...(visitableOnly ? { visitableOnly: true } : {})}
          {...(avoidableOnly ? { avoidableOnly: true } : {})}
          {...(categoryKeys?.length ? { categoryKeys } : {})}
          value={candidate}
          onChange={setCandidate}
          disabled={disabled || atLimit}
          helperText={
            atLimit
              ? `That is as many as this can take — ${max}`
              : taken
                ? taken.why
                : already
                  ? 'Already on the list'
                  : ''
          }
          {...(onExternalPick
            ? {
                onExternalPick: (place: ExternalPlace) => {
                  onExternalPick(place);
                  setCandidate(null);
                },
              }
            : {})}
        />
      </Box>
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        disabled={!candidate || already || atLimit || Boolean(taken)}
        onClick={add}
        // Lines up with the field rather than with its helper text.
        sx={{ flexShrink: 0, mt: 0.25 }}
      >
        Add
      </Button>
    </Stack>
  );
}
